import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, IActionFormProps, TableAdapter } from '@makyoapp/makyo-common'
import { Button, ButtonVariant, Field, Label } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { VariableColumnDd } from '@/components/VariableSelectV2/VariableColumnDd'

import { TableSelect } from '../../shared/TableSelect'

type ToggleValueFormValues = {
  // rowId: string
  tableId: string
  col: string
  isCurrentUser: boolean
}

export default function ActFormToggleValue({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  const { tables } = useDbClientContext()
  let tblOptions = tables.filter((tb) => !tb.isSnapshot)

  const { activeComponent, activeLayerContainer } = useActiveLayerContext()
  const isDetailpage = !!activeLayerContainer?.tableId

  // get the tableId (datasource) from 'list' component
  const isListOrComment = activeComponent?.groupId === 'list' || activeComponent?.compId === 'comment'
  const elSettings = !activeComponent ? undefined : activeLayerContainer.elementSettings?.[activeComponent.id]
  const elSettingsDataSourceKey = !elSettings
    ? undefined
    : Object.keys(elSettings).find((key) => key.includes('data-source'))
  const elSettingsTableId =
    elSettings && elSettingsDataSourceKey ? (elSettings[elSettingsDataSourceKey] as string) : undefined
  const elSettingsTable = elSettings && elSettingsTableId ? tables.find((tb) => tb.id === elSettingsTableId) : undefined

  // Get default tableId & col
  const defaultTableId = isDetailpage
    ? activeLayerContainer?.tableId
    : isListOrComment
      ? elSettingsTableId
      : tblOptions[0]?.id

  const defaultColumnId = tblOptions
    .find((tb) => tb.id === defaultTableId)
    ?.columns.filter((col) => [ColumnTypes.Boolean].includes(col.type))?.[0]?.id

  if (isListOrComment && elSettingsTable) {
    tblOptions = [
      {
        id: 'this-item',
        name: 'This Item',
        columns: elSettingsTable.columns,
      } as unknown as TableAdapter,
      ...tblOptions,
    ]
  }

  // access form store
  const { form, onSubmit: onFormSubmit } = useForm<ToggleValueFormValues>({
    defaultValues: {
      tableId: initialData?.tableId || defaultTableId || undefined,
      col: initialData?.col || defaultColumnId || undefined,
      isCurrentUser: initialData?.isCurrentUser ?? false,
    },
    validate(values, setError) {
      const tblOpts = tblOptions.find((tb) => tb.id === values.tableId)
      const colOpts = tblOpts?.columns?.filter((col) => [ColumnTypes.Boolean].includes(col.type)) || []

      setError(
        'col',
        !values.col
          ? !colOpts.length
            ? 'The table you selected does not contain any boolean columns. Please select another table.'
            : 'Please select a column'
          : null
      )
    },
  })

  // derived form state
  const currentTableId = form.getValues('tableId')
  const currentColId = form.getValues('col')
  const columnOptions = [
    { id: '', name: 'Select Column' } as ColumnAdapter,
    ...(tblOptions
      .find((tb) => tb.id === currentTableId)
      ?.columns?.filter((col) => [ColumnTypes.Boolean].includes(col.type)) || []),
  ]

  // change handler
  const handleChangeTable = (tb: TableAdapter) => {
    // get the first boolean column of selected table, if exist
    const firstColumnId = tables
      .find((tbl) => tbl.id === tb.id)
      ?.columns.filter((col) => [ColumnTypes.Boolean].includes(col.type))?.[0]?.id

    form.setValues((prev) => {
      return {
        ...prev,
        col: firstColumnId || '',
        tableId: tb.id,
        isCurrentUser: tb.id === window.appUserTableID,
      }
    })
  }

  // change column handler
  const handleChangeColumn = (colId: string) => {
    form.setValues('col', colId)
  }

  // save action settings
  const handleSubmit = onFormSubmit((data) => {
    onSubmit(data)
  })

  const invalidForm = !currentTableId || !currentColId || !!form.errors

  return (
    <div>
      <div className='p-4'>
        <Field className='min-h-0'>
          <Label required>Table</Label>

          <TableSelect
            options={tblOptions}
            value={tblOptions.find((tb) => tb.id === form.getValues('tableId'))}
            onChange={handleChangeTable}
            error={form.errors?.tableId}
          />
        </Field>

        <Field className='min-h-0'>
          <Label required>Column</Label>

          <div className='flex-1'>
            <VariableColumnDd
              setCol={handleChangeColumn}
              col={form.getValues('col')}
              colOptions={columnOptions}
              className='px-0!'
            />
            <div className='mk-input-error'>{form.errors?.col}</div>
          </div>
        </Field>
      </div>

      <div className='px-4 py-2 border-t bdr flex justify-between items-center'>
        <Button variant={ButtonVariant.Outlined} onClick={onDismiss}>
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save</Trans>
        </Button>
      </div>
    </div>
  )
}
